import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Leaf,
  LogOut,
  Search,
  Download,
  RefreshCw,
  Mail,
  Phone,
  Trash2,
  Filter,
  Inbox,
  CheckCircle2,
  Archive,
  Loader2,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { useAdminAuth, API } from "./AdminAuthContext";

const STATUS_OPTIONS = [
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "archived", label: "Archived" },
];

function StatusBadge({ status }) {
  const map = {
    new: "bg-accent/30 text-accent-foreground border-accent/50",
    contacted: "bg-primary/10 text-primary border-primary/30",
    archived: "bg-muted text-muted-foreground border-border",
  };
  const cls = map[status] || map.new;
  return (
    <Badge
      data-testid={`status-badge-${status}`}
      variant="outline"
      className={`${cls} capitalize font-medium`}
    >
      {status}
    </Badge>
  );
}

function StatCard({ label, value, icon: Icon, testId }) {
  return (
    <div
      data-testid={testId}
      className="rounded-xl border border-border bg-white p-5"
    >
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">
          {label}
        </p>
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Icon className="h-4 w-4" />
        </span>
      </div>
      <p className="mt-3 font-display font-bold text-3xl text-foreground">
        {value}
      </p>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function AdminDashboard() {
  const { user, checking, logout, authHeader, token } = useAdminAuth();
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, archived: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (search.trim()) params.q = search.trim();
      const [listRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/submissions`, { params, headers: authHeader }),
        axios.get(`${API}/admin/submissions/stats`, { headers: authHeader }),
      ]);
      setItems(listRes.data);
      setStats(statsRes.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        logout();
        navigate("/admin", { replace: true });
      } else {
        toast.error("Failed to load submissions");
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, search, token]);

  useEffect(() => {
    if (!checking && user) fetchAll();
  }, [checking, user, fetchAll]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/submissions/${id}`, { status }, { headers: authHeader });
      toast.success(`Marked as ${status}`);
      setSelected((s) => (s && s.id === id ? { ...s, status } : s));
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, status } : it)));
      fetchAll();
    } catch {
      toast.error("Failed to update status");
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await axios.delete(`${API}/admin/submissions/${deleting.id}`, { headers: authHeader });
      toast.success("Submission deleted");
      if (selected && selected.id === deleting.id) setSelected(null);
      setDeleting(null);
      fetchAll();
    } catch {
      toast.error("Failed to delete submission");
    }
  };

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/submissions/export.csv`, {
        headers: authHeader,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `gees-enquiries-${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch {
      toast.error("Failed to export CSV");
    }
  };

  const filteredCount = useMemo(() => items.length, [items]);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }
  if (!user) return <Navigate to="/admin" replace />;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-secondary/30">
      <header className="bg-white border-b border-border sticky top-0 z-30">
        <div className="container-wide flex items-center justify-between h-16">
          <div className="flex items-center gap-2.5">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-primary text-primary-foreground">
              <Leaf className="h-5 w-5" />
            </span>
            <div>
              <p className="font-display font-extrabold text-primary text-base leading-none">
                GEES Admin
              </p>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                {user.email}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchAll()}
              data-testid="admin-refresh-btn"
              className="rounded-full"
            >
              <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                logout();
                navigate("/admin");
              }}
              data-testid="admin-logout-btn"
              className="rounded-full"
            >
              <LogOut className="h-4 w-4 mr-1.5" />
              Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="container-wide py-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard label="Total" value={stats.total} icon={Inbox} testId="stat-total" />
          <StatCard label="New" value={stats.new} icon={Mail} testId="stat-new" />
          <StatCard label="Contacted" value={stats.contacted} icon={CheckCircle2} testId="stat-contacted" />
          <StatCard label="Archived" value={stats.archived} icon={Archive} testId="stat-archived" />
        </div>

        <div className="rounded-2xl bg-white border border-border overflow-hidden">
          <div className="p-5 border-b border-border flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
            <div className="flex flex-1 items-center gap-3 max-w-2xl">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search name, email, subject, message..."
                  data-testid="admin-search-input"
                  className="pl-9 h-10"
                />
              </div>
              <div className="w-44">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger data-testid="admin-status-filter" className="h-10">
                    <Filter className="h-4 w-4 mr-1.5" />
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    {STATUS_OPTIONS.map((s) => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button
              onClick={exportCsv}
              data-testid="admin-export-csv-btn"
              className="bg-primary hover:bg-primary/90 rounded-full"
            >
              <Download className="h-4 w-4 mr-1.5" />
              Export CSV
            </Button>
          </div>

          <div className="overflow-x-auto" data-testid="admin-leads-table">
            <table className="w-full text-sm">
              <thead className="bg-secondary/60 text-foreground">
                <tr>
                  <th className="text-left font-semibold px-5 py-3">Lead</th>
                  <th className="text-left font-semibold px-5 py-3 hidden md:table-cell">Subject</th>
                  <th className="text-left font-semibold px-5 py-3 hidden lg:table-cell">Received</th>
                  <th className="text-left font-semibold px-5 py-3">Status</th>
                  <th className="text-right font-semibold px-5 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && items.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-5 py-16 text-center text-muted-foreground">
                      <Loader2 className="inline h-5 w-5 animate-spin mr-2" />
                      Loading…
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-5 py-16 text-center text-muted-foreground">
                      No submissions match your filter.
                    </td>
                  </tr>
                ) : (
                  items.map((it) => (
                    <tr
                      key={it.id}
                      data-testid={`lead-row-${it.id}`}
                      className="border-t border-border hover:bg-secondary/40 cursor-pointer"
                      onClick={() => setSelected(it)}
                    >
                      <td className="px-5 py-4">
                        <p className="font-semibold text-foreground">{it.name}</p>
                        <p className="text-xs text-muted-foreground">{it.email}</p>
                      </td>
                      <td className="px-5 py-4 hidden md:table-cell text-foreground/80">
                        <span className="line-clamp-1">{it.subject || "—"}</span>
                      </td>
                      <td className="px-5 py-4 hidden lg:table-cell text-muted-foreground text-xs">
                        {formatDate(it.created_at)}
                      </td>
                      <td className="px-5 py-4">
                        <StatusBadge status={it.status || "new"} />
                      </td>
                      <td className="px-5 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="inline-flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => updateStatus(it.id, "contacted")}
                            data-testid={`mark-contacted-${it.id}`}
                            className="text-primary hover:text-primary hover:bg-primary/10"
                          >
                            <CheckCircle2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleting(it)}
                            data-testid={`delete-${it.id}`}
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="p-4 text-xs text-muted-foreground border-t border-border">
            Showing {filteredCount} of {stats.total} submissions
          </div>
        </div>
      </main>

      {/* Detail drawer */}
      {selected && (
        <div
          data-testid="lead-detail-drawer"
          className="fixed inset-0 z-40 bg-foreground/40 backdrop-blur-sm flex justify-end"
          onClick={() => setSelected(null)}
        >
          <div
            className="w-full max-w-xl h-full bg-white shadow-2xl overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-border flex items-start justify-between gap-4">
              <div>
                <StatusBadge status={selected.status || "new"} />
                <h3 className="mt-3 font-display font-bold text-2xl text-foreground">
                  {selected.name}
                </h3>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {formatDate(selected.created_at)}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelected(null)}
                data-testid="close-drawer"
              >
                Close
              </Button>
            </div>

            <div className="p-6 space-y-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <a
                  href={`mailto:${selected.email}`}
                  className="rounded-lg border border-border p-3 hover:border-primary/40 transition-colors"
                >
                  <p className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground flex items-center gap-1.5">
                    <Mail className="h-3.5 w-3.5" /> Email
                  </p>
                  <p className="mt-1 text-sm text-foreground break-all">{selected.email}</p>
                </a>
                <a
                  href={selected.phone ? `tel:${selected.phone}` : undefined}
                  className="rounded-lg border border-border p-3 hover:border-primary/40 transition-colors"
                >
                  <p className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground flex items-center gap-1.5">
                    <Phone className="h-3.5 w-3.5" /> Phone
                  </p>
                  <p className="mt-1 text-sm text-foreground">{selected.phone || "—"}</p>
                </a>
              </div>

              <div>
                <p className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">
                  Subject
                </p>
                <p className="mt-1 text-sm text-foreground">{selected.subject || "—"}</p>
              </div>

              <div>
                <p className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">
                  Message
                </p>
                <p
                  data-testid="lead-detail-message"
                  className="mt-1 text-sm text-foreground whitespace-pre-wrap leading-relaxed"
                >
                  {selected.message}
                </p>
              </div>

              <div>
                <p className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground mb-2">
                  Update status
                </p>
                <div className="flex flex-wrap gap-2">
                  {STATUS_OPTIONS.map((s) => (
                    <Button
                      key={s.value}
                      variant={selected.status === s.value ? "default" : "outline"}
                      size="sm"
                      onClick={() => updateStatus(selected.id, s.value)}
                      data-testid={`set-status-${s.value}`}
                      className={
                        selected.status === s.value
                          ? "bg-primary hover:bg-primary/90 rounded-full"
                          : "rounded-full"
                      }
                    >
                      {s.label}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="pt-3 border-t border-border flex justify-between">
                <Button
                  variant="outline"
                  onClick={() => {
                    window.location.href = `mailto:${selected.email}?subject=Re: ${encodeURIComponent(selected.subject || "Your enquiry")}`;
                  }}
                  data-testid="reply-via-email"
                  className="rounded-full"
                >
                  <Mail className="h-4 w-4 mr-1.5" />
                  Reply
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setDeleting(selected)}
                  data-testid="delete-from-drawer"
                  className="text-destructive border-destructive/30 hover:bg-destructive/5 rounded-full"
                >
                  <Trash2 className="h-4 w-4 mr-1.5" />
                  Delete
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this submission?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the enquiry from{" "}
              <span className="font-semibold text-foreground">{deleting?.name}</span>.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              data-testid="delete-confirm"
              className="bg-destructive hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
